import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Typography, Space, Input, Row, Col } from 'antd';

import { CommonFormProps, OnboardingLayout } from '.';
import { BackButton, Form } from '~/components';
import { authenticateSmsOtp, getAlreadyFilledData } from '~/api/onboarding';
import { auth } from '~/api/axios';
import { useOnboardingPage, useOnboardingState, useSkipSteps } from '~/hooks/onboarding';
import onError from '~/errorsHandler';

const { Title, Text } = Typography;

const ConfirmPhoneNumber: FC<CommonFormProps> = (props) => {
  const { name } = props;
  const [isLoading, setLoading] = useState(false);
  const [tooManyAttempts, setTooManyAttempts] = useState(false);

  const [, setPageState] = useOnboardingState(name);
  const [personalInfoState] = useOnboardingState('personalInfo');

  const { challengeToken } = personalInfoState.relatedData || ({} as any);
  const { firstName, lastName, phoneNumber } = personalInfoState.formData || {};

  const onboardingPage = useOnboardingPage(name);

  const { t } = useTranslation('onboarding');
  const dt = (key: string): string => t(`translation:${key}`);
  const [form] = Form.useForm();

  const skipHandlers = useSkipSteps();

  const onFinish = async (values: any) => {
    const { otp } = values;
    setLoading(true);
    try {
      const { headers } = await authenticateSmsOtp(challengeToken, otp, `${firstName} ${lastName}`);
      auth.setToken(headers, 'smsOtp');
      const { data: alreadyFilled } = await getAlreadyFilledData();

      // reset relatedData
      skipHandlers.businessCreated(false);
      skipHandlers.email(false);
      skipHandlers.password(false);
      skipHandlers.accountExist(false);
      //
      if (alreadyFilled.emailAddress) {
        skipHandlers.email(true);
      }
      if (alreadyFilled.hasPassword) {
        skipHandlers.authenticate(false);
        skipHandlers.password(true);
      }

      setPageState((prev) => ({
        ...prev,
        formData: values,
        disabled: true,
      }));
      onboardingPage.activateNext();
      onboardingPage.goNext();
    } catch (error: any) {
      onError(error);
      if ((error as any)?.response?.data?.message?.indexOf('Too many attempts') >= 0) {
        setTooManyAttempts(true);
        onboardingPage.goBack();
      }
    }
    setLoading(false);
  };

  return (
    <OnboardingLayout>
      <Title level={2}>{t('confirmPhoneHeader')}</Title>
      <Space size="middle" direction="vertical">
        <Text>
          {t('confirmPhoneSubtitle')} <bdi>{phoneNumber}</bdi>
        </Text>
        <Form form={form} layout="vertical" size="large" onFinish={onFinish}>
          <Form.Item name="otp" rules={[{ required: true, min: 5 }]}>
            <Input placeholder={t('confirmPhoneInputPlaceholder')} />
          </Form.Item>
          <Row gutter={16}>
            <Col>
              <BackButton onClick={onboardingPage.goBack} />
            </Col>
            <Col>
              <Form.Item noStyle>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                  disabled={tooManyAttempts}
                >
                  {dt('continue')}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Space>
    </OnboardingLayout>
  );
};

export default ConfirmPhoneNumber;
